export const MMS_TARGET_IMAGE_BYTES = 600 * 1024;
export const MMS_MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const COMPRESSION_PROFILES = [
  { maxDimension: 1600, quality: 0.74 },
  { maxDimension: 1400, quality: 0.66 },
  { maxDimension: 1200, quality: 0.58 },
  { maxDimension: 1000, quality: 0.5 },
  { maxDimension: 800, quality: 0.42 },
  { maxDimension: 640, quality: 0.35 },
];

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => resolve({ image, url });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("The selected image could not be read."));
    };
    image.src = url;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("The selected image could not be compressed.")),
      "image/jpeg",
      quality,
    );
  });

export const optimizeMessageImage = async (file) => {
  if (!file?.type?.startsWith("image/")) return file;

  if (
    ["image/jpeg", "image/png", "image/gif"].includes(file.type) &&
    file.size <= MMS_TARGET_IMAGE_BYTES
  ) {
    return file;
  }

  if (file.type === "image/gif") {
    if (file.size <= MMS_MAX_IMAGE_BYTES) return file;
    throw new Error("This animated image is too large to send. Please choose a smaller image.");
  }

  const { image, url } = await loadImage(file);
  let optimizedFile;

  try {
    for (const profile of COMPRESSION_PROFILES) {
      const scale = Math.min(1, profile.maxDimension / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("The selected image could not be compressed.");
      }
      context.fillStyle = "#fff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await canvasToBlob(canvas, profile.quality);
      const shortName = `image-${Date.now().toString(36)}.jpg`;
      optimizedFile = new File([blob], shortName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      if (optimizedFile.size <= MMS_TARGET_IMAGE_BYTES) break;
    }
  } finally {
    URL.revokeObjectURL(url);
  }

  if (!optimizedFile || optimizedFile.size > MMS_MAX_IMAGE_BYTES) {
    throw new Error("The image is too large to send. Please choose a smaller image.");
  }

  return optimizedFile;
};
